import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router';
import {
  patchQuickBookmark,
  postQuickBookmark,
  requestQuickBookmarks,
} from '../../api/requests/quickBookmarks/requestQuickBookmarks';
import { useDatasetContext } from '../../hooks/context/useDatasetContext';
import { useGuideContext } from '../GuideContext/GuideContext';
import { useSelectedRowsContext } from '../../hooks/context/useSelectedRowsContext';
import { useTranslate } from '../../hooks/context/useTranslate';
import { useUserState } from '../../hooks/context/useUserState';
import {
  QuickBookmark,
  QuickBookmarkDraft,
  QuickBookmarkNavigation,
  QuickBookmarkStatus,
} from '../../types/quickBookmarks';
import { activateGuideTarget } from '../../utils/guides/resolveGuideTarget';

export type QuickBookmarksView = 'closed' | 'list' | 'create';

interface QuickBookmarksContextValue {
  items: QuickBookmark[];
  view: QuickBookmarksView;
  draftNavigation: QuickBookmarkNavigation | null;
  dueBookmark: QuickBookmark | null;
  loading: boolean;
  saving: boolean;
  error: boolean;
  openCreate: () => void;
  openList: () => void;
  close: () => void;
  create: (draft: QuickBookmarkDraft) => Promise<boolean>;
  update: (
    id: string,
    patch: { reminderAt?: string | null; status?: QuickBookmarkStatus }
  ) => Promise<boolean>;
  show: (bookmark: QuickBookmark) => void;
  reload: () => void;
}

const defaultValue: QuickBookmarksContextValue = {
  items: [],
  view: 'closed',
  draftNavigation: null,
  dueBookmark: null,
  loading: false,
  saving: false,
  error: false,
  openCreate: () => undefined,
  openList: () => undefined,
  close: () => undefined,
  create: async () => false,
  update: async () => false,
  show: () => undefined,
  reload: () => undefined,
};

const getPathItemId = (pathname: string): string | null => {
  const actionMatch = pathname.match(/\/(?:edit|copy)\/([^/]+)/u);
  if (actionMatch) return decodeURIComponent(actionMatch[1]);
  const inlineDetailMatch = pathname.match(/\/item\/([^/]+)/u);
  if (inlineDetailMatch) return decodeURIComponent(inlineDetailMatch[1]);
  const detailMatch = pathname.match(/\/detail\/([^/]+)/u);
  return detailMatch ? decodeURIComponent(detailMatch[1]) : null;
};

const needsInlineDetailRestore = (pathname: string): boolean =>
  /^\/dataset\/[^/]+\/item\/[^/]+(?:\/subset\/[^/]+)?$/u.test(pathname);

const MAX_FILTER_SNAPSHOT_LENGTH = 50_000;

const QuickBookmarksProvider = ({ children }: { children: ReactNode }) => {
  const {
    state: { token },
  } = useUserState();
  const { pathname } = useLocation();
  const { dataset, data, activeFilters } = useDatasetContext();
  const { expandedRow, selectedRows, selectedSlaveRows } =
    useSelectedRowsContext();
  const { t } = useTranslate();
  const { closePanel, navigateToUrl } = useGuideContext();
  const [items, setItems] = useState<QuickBookmark[]>([]);
  const [view, setView] = useState<QuickBookmarksView>('closed');
  const [draftNavigation, setDraftNavigation] =
    useState<QuickBookmarkNavigation | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [reloadVersion, setReloadVersion] = useState(0);
  const [clock, setClock] = useState(() => Date.now());
  const serverOffsetRef = useRef(0);
  const startupTokenRef = useRef<string | null>(null);

  const captureNavigation = useCallback((): QuickBookmarkNavigation | null => {
    if (!pathname.startsWith('/')) return null;
    const sectionTrigger = document.querySelector<HTMLElement>(
      '.form-section-container .p-highlight [data-guide-form-section-trigger]'
    );
    const sectionId =
      sectionTrigger?.getAttribute('data-guide-form-section-trigger') ?? null;
    const displayPath: string[] = [];
    if (dataset?.label) displayPath.push(String(dataset.label));

    const itemId = getPathItemId(pathname);
    const dataKey = dataset?.idItem;
    const candidates = [
      ...selectedSlaveRows,
      ...selectedRows,
      ...(expandedRow ? [expandedRow] : []),
      ...(data ?? []),
    ];
    const row =
      itemId && dataKey
        ? candidates.find(
            (candidate) => String(candidate?.[dataKey]) === itemId
          )
        : candidates[0];
    const rowLabel = dataset?.labelItem && row ? row[dataset.labelItem] : null;
    if (
      rowLabel !== null &&
      rowLabel !== undefined &&
      String(rowLabel).trim()
    ) {
      displayPath.push(String(rowLabel));
    }
    if (pathname.includes('/edit/')) displayPath.push(t('quickBookmarks.edit'));
    else if (pathname.includes('/copy/'))
      displayPath.push(t('quickBookmarks.copy'));
    else if (pathname.includes('/add'))
      displayPath.push(t('quickBookmarks.add'));
    else if (pathname.includes('/detail/'))
      displayPath.push(t('quickBookmarks.detail'));
    if (sectionTrigger?.textContent?.trim()) {
      displayPath.push(sectionTrigger.textContent.trim());
    }
    if (displayPath.length === 0)
      displayPath.push(t('quickBookmarks.currentPage'));
    let filters: Record<string, unknown> | undefined;
    if (activeFilters !== null && Object.keys(activeFilters).length <= 100) {
      try {
        const serialized = JSON.stringify(activeFilters);
        if (serialized.length <= MAX_FILTER_SNAPSHOT_LENGTH) {
          filters = JSON.parse(serialized);
        }
      } catch {
        filters = undefined;
      }
    }
    return { path: pathname, displayPath, sectionId, filters };
  }, [
    activeFilters,
    data,
    dataset,
    expandedRow,
    pathname,
    selectedRows,
    selectedSlaveRows,
    t,
  ]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(false);
    try {
      const response = await requestQuickBookmarks(token);
      serverOffsetRef.current = Date.parse(response.serverNow) - Date.now();
      setItems(response.items);
      setClock(Date.now() + serverOffsetRef.current);
      if (startupTokenRef.current !== token) {
        startupTokenRef.current = token;
        if (response.items.length > 0) {
          setView((current) => (current === 'closed' ? 'list' : current));
        }
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setItems([]);
      setView('closed');
      startupTokenRef.current = null;
      return;
    }
    void load();
  }, [load, reloadVersion, token]);

  useEffect(() => {
    const updateClock = () => setClock(Date.now() + serverOffsetRef.current);
    const interval = window.setInterval(updateClock, 30_000);
    window.addEventListener('focus', updateClock);
    document.addEventListener('visibilitychange', updateClock);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', updateClock);
      document.removeEventListener('visibilitychange', updateClock);
    };
  }, []);

  const openCreate = useCallback(() => {
    closePanel();
    setError(false);
    setDraftNavigation(captureNavigation());
    setView('create');
  }, [captureNavigation, closePanel]);

  const openList = useCallback(() => {
    closePanel();
    setView('list');
  }, [closePanel]);

  const close = useCallback(() => setView('closed'), []);

  const create = useCallback(
    async (draft: QuickBookmarkDraft): Promise<boolean> => {
      if (!token) return false;
      setSaving(true);
      setError(false);
      try {
        const response = await postQuickBookmark(token, draft);
        setItems((current) => [...current, response.item]);
        setView('list');
        return true;
      } catch {
        setError(true);
        return false;
      } finally {
        setSaving(false);
      }
    },
    [token]
  );

  const update = useCallback(
    async (
      id: string,
      patch: { reminderAt?: string | null; status?: QuickBookmarkStatus }
    ): Promise<boolean> => {
      if (!token) return false;
      setSaving(true);
      setError(false);
      try {
        const response = await patchQuickBookmark(token, id, patch);
        setItems((current) =>
          response.item.status === 'open'
            ? current.map((item) => (item.id === id ? response.item : item))
            : current.filter((item) => item.id !== id)
        );
        return true;
      } catch {
        setError(true);
        return false;
      } finally {
        setSaving(false);
      }
    },
    [token]
  );

  const show = useCallback(
    (bookmark: QuickBookmark) => {
      if (!bookmark.navigation) return;
      const targetPath = bookmark.navigation.path;
      navigateToUrl(
        targetPath,
        () => {
          setView('closed');
          if (!bookmark.navigation?.sectionId) return;
          window.setTimeout(
            () =>
              void activateGuideTarget({
                kind: 'section',
                name: bookmark.navigation!.sectionId!,
              }),
            200
          );
        },
        bookmark.navigation.filters !== undefined ||
          needsInlineDetailRestore(targetPath)
          ? {
              ...(needsInlineDetailRestore(targetPath)
                ? { forceFindInDataset: true }
                : {}),
              ...(bookmark.navigation.filters !== undefined
                ? { quickBookmarkFilters: bookmark.navigation.filters }
                : {}),
            }
          : undefined
      );
    },
    [navigateToUrl]
  );

  const dueBookmark = useMemo(
    () =>
      items
        .filter(
          (item) =>
            item.reminderAt !== null && Date.parse(item.reminderAt) <= clock
        )
        .sort(
          (left, right) =>
            Date.parse(left.reminderAt!) - Date.parse(right.reminderAt!)
        )[0] ?? null,
    [clock, items]
  );

  const value = useMemo<QuickBookmarksContextValue>(
    () => ({
      items,
      view,
      draftNavigation,
      dueBookmark,
      loading,
      saving,
      error,
      openCreate,
      openList,
      close,
      create,
      update,
      show,
      reload: () => setReloadVersion((current) => current + 1),
    }),
    [
      items,
      view,
      draftNavigation,
      dueBookmark,
      loading,
      saving,
      error,
      openCreate,
      openList,
      close,
      create,
      update,
      show,
    ]
  );

  return (
    <QuickBookmarksContext.Provider value={value}>
      {children}
    </QuickBookmarksContext.Provider>
  );
};

const QuickBookmarksContext = createContext(defaultValue);

export const useQuickBookmarks = (): QuickBookmarksContextValue =>
  useContext(QuickBookmarksContext);

export default QuickBookmarksProvider;
